import { useEffect, useState, Component } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Search, ArrowLeft, Trash2, Edit, FileText, Receipt, Paperclip, Upload, X, Send, CheckCircle2, Ban, Lock, Save, AlertTriangle, Briefcase, Sparkles, Repeat, UserPlus, ChevronRight, Zap, MoreVertical, Filter, ArrowUpDown } from "lucide-react";
import QuickQuoteModal from "../components/QuickQuoteModal";
import ConfirmDialog from "../components/ConfirmDialog";
import { ExportImportBar } from "../components/ExportImportBar";
import api, { formatErr, fileUrl } from "../lib/apiClient";
import { fmtCAD, fmtDate, fmtDateTime, statusClass } from "../lib/format";

export function CustomersList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDlg, setConfirmDlg] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (type !== "all") params.set("customer_type", type);
      const { data } = await api.get(`/customers?${params}`);
      setItems(data);
      // Drop any selections for rows no longer present
      setSelected((prev) => {
        const stillPresent = new Set(data.map((c) => c.id));
        const next = new Set();
        prev.forEach((id) => { if (stillPresent.has(id)) next.add(id); });
        return next;
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [search, type]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === items.length ? new Set() : new Set(items.map((c) => c.id))));
  };
  const clearSelection = () => setSelected(new Set());

  const askBatchDelete = () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setConfirmDlg({
      title: `Delete ${ids.length} customer${ids.length === 1 ? "" : "s"}?`,
      body: "Historical jobs, invoices and estimates for these customers remain in reporting. This cannot be undone from the UI.",
      confirmLabel: `Delete ${ids.length}`,
      onConfirm: async () => {
        setDeleting(true);
        try {
          await api.post("/customers/batch-delete", { ids });
          clearSelection();
          await load();
        } finally { setDeleting(false); }
      },
    });
  };

  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <div data-testid="customers-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} customer{items.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <ExportImportBar entity="customers" onImportComplete={load} />
          <Link to="/customers/new" data-testid="add-customer-btn" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2">
            <Plus size={16} /> Add Customer
          </Link>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input data-testid="customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]" />
        </div>
        <select data-testid="customer-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
          <option value="all">All types</option>
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
        </select>
      </div>

      {selected.size > 0 && (
        <div data-testid="customer-selection-bar" className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 border border-blue-200 rounded-md text-sm">
          <span className="font-medium text-slate-800">{selected.size} selected</span>
          <button onClick={clearSelection} className="text-[#0F4C81] hover:underline" data-testid="customer-selection-clear">clear</button>
          <div className="flex-1" />
          <button
            onClick={askBatchDelete}
            disabled={deleting}
            data-testid="customer-batch-delete-btn"
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md font-medium flex items-center gap-1.5 disabled:opacity-60"
          >
            <Trash2 size={14} /> {deleting ? "Deleting…" : `Delete ${selected.size}`}
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={items.length === 0}
                    data-testid="customer-select-all"
                    className="w-4 h-4 cursor-pointer"
                    aria-label="Select all customers"
                  />
                </th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Name</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Type</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Email / Phone</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Address</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium hidden lg:table-cell">Tags</th>
                <th className="px-3 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No customers yet. Click &quot;Add Customer&quot; to get started.</td></tr>}
              {items.map((c) => {
                const isSel = selected.has(c.id);
                return (
                  <tr
                    key={c.id}
                    data-testid={`customer-row-${c.id}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${isSel ? "bg-blue-50/40" : ""}`}
                    onClick={() => nav(`/customers/${c.id}`)}
                  >
                    <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); toggleOne(c.id); }}>
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleOne(c.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`customer-select-${c.id}`}
                        className="w-4 h-4 cursor-pointer"
                        aria-label={`Select ${c.name}`}
                      />
                    </td>
                    <td className="px-4 py-3"><div className="font-medium text-slate-900">{c.name}</div>{c.company_name && <div className="text-xs text-slate-500">{c.company_name}</div>}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${c.customer_type === "commercial" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700"}`}>{c.customer_type}</span></td>
                    <td className="px-4 py-3"><div className="text-slate-700">{c.email || "—"}</div><div className="text-xs text-slate-500">{c.phone}</div></td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-600 max-w-xs truncate">{c.billing_address || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">{c.tags?.map((t) => <span key={t} className="text-xs px-2 py-0.5 mr-1 bg-[#E0F2FE] text-[#0F4C81] rounded">{t}</span>)}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDlg({
                            title: `Delete customer ${c.name}?`,
                            body: "Historical jobs, invoices and estimates remain in reporting. This cannot be undone from the UI.",
                            confirmLabel: "Delete customer",
                            onConfirm: async () => {
                              await api.delete(`/customers/${c.id}`);
                              await load();
                            },
                          });
                        }}
                        data-testid={`customer-delete-${c.id}`}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete customer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog dialog={confirmDlg} onClose={() => setConfirmDlg(null)} />
    </div>
  );
}

const CA_PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];

const _emptyPhone = () => ({ label: "mobile", number: "" });
const _emptyAddress = () => ({ street: "", unit: "", municipality: "", province: "ON", postal_code: "", notes: "" });

// Part 26.1 (Feb 2026) — bug fix: `Section` and `Field` MUST live at module scope,
// not inside `CustomerForm`. When defined inside, every keystroke re-runs the parent
// render → new function references → React sees a different component type on each
// render → unmounts and remounts every wrapped <input> → focus is lost after each
// keystroke. Same trap the codebase hit before with dialog helpers.
function CustSection({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-5 py-3 border-b border-slate-100">
        <h3 className="font-display font-semibold text-slate-900 text-sm uppercase tracking-wider">{title}</h3>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function CustField({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-medium text-slate-700 uppercase tracking-wider block mb-1">{label}</label>
      {children}
    </div>
  );
}

const _inputCls = "w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]";

export function CustomerForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  // Part 26 (Feb 2026) — new customer form matches user's HCP screenshots.
  // Fields: contact info (name, display, emails[], phones[], company, role,
  // homeowner/business, do-not-service), addresses[], notes, bills-to.
  // Excluded per user: tags, lead source, referred by.
  const [data, setData] = useState({
    first_name: "", last_name: "", display_name: "",
    emails: [""],
    phones: [_emptyPhone(), { label: "home", number: "" }, { label: "work", number: "" }],
    company_name: "", role: "",
    customer_type: "residential",
    do_not_service: false,
    do_not_service_reason: "",
    addresses: [_emptyAddress()],
    notes: "",
    bills_to: "",
    send_notifications: true,
  });
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isNew) {
      api.get(`/customers/${id}`).then(({ data: c }) => {
        setData({
          first_name: c.first_name || "",
          last_name: c.last_name || "",
          display_name: c.display_name || c.name || "",
          emails: (c.emails && c.emails.length) ? c.emails : [c.email || ""],
          phones: (c.phones && c.phones.length) ? c.phones : [
            { label: "mobile", number: c.phone || "" },
            { label: "home", number: "" },
            { label: "work", number: "" },
          ],
          company_name: c.company_name || "",
          role: c.role || "",
          customer_type: c.customer_type || "residential",
          do_not_service: !!c.do_not_service,
          do_not_service_reason: c.do_not_service_reason || "",
          addresses: (c.addresses && c.addresses.length) ? c.addresses : [_emptyAddress()],
          notes: c.notes || "",
          bills_to: c.bills_to || "",
          send_notifications: c.send_notifications !== false,
        });
      });
    }
  }, [id, isNew]);

  const setPhone = (i, patch) => setData((d) => {
    const arr = [...d.phones]; arr[i] = { ...arr[i], ...patch }; return { ...d, phones: arr };
  });
  const setEmail = (i, v) => setData((d) => { const arr = [...d.emails]; arr[i] = v; return { ...d, emails: arr }; });
  const setAddr = (i, patch) => setData((d) => { const arr = [...d.addresses]; arr[i] = { ...arr[i], ...patch }; return { ...d, addresses: arr }; });

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...data };
      if (isNew) {
        const { data: c } = await api.post("/customers", payload);
        nav(`/customers/${c.id}`);
      } else {
        await api.put(`/customers/${id}`, payload);
        nav(`/customers/${id}`);
      }
    } catch (e) { setErr(formatErr(e)); }
  };

  return (
    <form onSubmit={save} data-testid="customer-form" className="space-y-5 max-w-4xl">
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => nav(-1)} className="p-2 hover:bg-slate-100 rounded-md"><ArrowLeft size={18} /></button>
        <h1 className="font-display text-2xl font-bold tracking-tight">{isNew ? "Add new customer" : "Edit customer"}</h1>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{err}</div>}

      <CustSection title="Contact info">
        <div className="grid sm:grid-cols-2 gap-4">
          <CustField label="First name">
            <input value={data.first_name} onChange={(e) => setData({ ...data, first_name: e.target.value })} data-testid="customer-first-name" className={_inputCls} />
          </CustField>
          <CustField label="Last name">
            <input value={data.last_name} onChange={(e) => setData({ ...data, last_name: e.target.value })} data-testid="customer-last-name" className={_inputCls} />
          </CustField>
        </div>
        <CustField label="Display name (shown on invoices)">
          <input value={data.display_name} onChange={(e) => setData({ ...data, display_name: e.target.value })} data-testid="customer-display-name" placeholder="Falls back to First + Last if blank" className={_inputCls} />
        </CustField>

        {data.emails.map((em, i) => (
          <CustField key={`em-${i}`} label={i === 0 ? "Email" : `Email ${i + 1}`}>
            <div className="flex gap-2">
              <input type="email" value={em} onChange={(e) => setEmail(i, e.target.value)} data-testid={`customer-email-${i}`} className={_inputCls} />
              {data.emails.length > 1 && <button type="button" onClick={() => setData({ ...data, emails: data.emails.filter((_, x) => x !== i) })} className="p-2 text-slate-500 hover:bg-slate-100 rounded" data-testid={`customer-email-remove-${i}`}><X size={16} /></button>}
            </div>
          </CustField>
        ))}
        <button type="button" onClick={() => setData({ ...data, emails: [...data.emails, ""] })} data-testid="add-email-btn" className="text-xs font-medium text-[#0F4C81] hover:underline">+ Email</button>

        {data.phones.map((p, i) => (
          <div key={`ph-${i}`} className="grid grid-cols-[100px_1fr_44px] gap-2 items-end">
            <CustField label={i === 0 ? "Phone" : ""}>
              <select value={p.label} onChange={(e) => setPhone(i, { label: e.target.value })} data-testid={`customer-phone-label-${i}`} className={_inputCls}>
                <option value="mobile">Mobile</option><option value="home">Home</option>
                <option value="work">Work</option><option value="other">Other</option>
              </select>
            </CustField>
            <CustField label={i === 0 ? "\u00A0" : ""}>
              <input value={p.number} onChange={(e) => setPhone(i, { number: e.target.value })} data-testid={`customer-phone-${i}`} className={_inputCls} />
            </CustField>
            {data.phones.length > 1 && (
              <button type="button" onClick={() => setData({ ...data, phones: data.phones.filter((_, x) => x !== i) })} className="p-2 text-slate-500 hover:bg-slate-100 rounded mb-0" data-testid={`customer-phone-remove-${i}`}><X size={16} /></button>
            )}
            {data.phones.length === 1 && <div />}
          </div>
        ))}
        <button type="button" onClick={() => setData({ ...data, phones: [...data.phones, _emptyPhone()] })} data-testid="add-phone-btn" className="text-xs font-medium text-[#0F4C81] hover:underline">+ Phone</button>

        <div className="grid sm:grid-cols-2 gap-4">
          <CustField label="Company"><input value={data.company_name} onChange={(e) => setData({ ...data, company_name: e.target.value })} data-testid="customer-company" className={_inputCls} /></CustField>
          <CustField label="Role"><input value={data.role} onChange={(e) => setData({ ...data, role: e.target.value })} data-testid="customer-role" className={_inputCls} placeholder="e.g. Property Manager" /></CustField>
        </div>

        <CustField label="Customer type">
          <div className="flex items-center gap-6 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="ctype" checked={data.customer_type === "residential"} onChange={() => setData({ ...data, customer_type: "residential" })} data-testid="customer-type-homeowner" />
              Homeowner
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="ctype" checked={data.customer_type === "commercial"} onChange={() => setData({ ...data, customer_type: "commercial" })} data-testid="customer-type-business" />
              Business
            </label>
          </div>
        </CustField>

        <label className="flex items-start gap-2 cursor-pointer p-3 bg-amber-50 border border-amber-200 rounded" data-testid="customer-dns-toggle-wrap">
          <input type="checkbox" checked={data.do_not_service} onChange={(e) => setData({ ...data, do_not_service: e.target.checked })} data-testid="customer-do-not-service" className="mt-0.5"/>
          <div className="text-xs">
            <div className="font-medium text-amber-900">Mark as Do not service</div>
            <div className="text-amber-800">Turns off notifications and blocks scheduling for this customer.</div>
          </div>
        </label>
      </CustSection>

      <CustSection title="Address">
        {data.addresses.map((a, i) => (
          <div key={`addr-${i}`} className="space-y-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
              <CustField label={i === 0 ? "Street" : `Street ${i + 1}`}><input value={a.street} onChange={(e) => setAddr(i, { street: e.target.value })} data-testid={`customer-address-street-${i}`} className={_inputCls} /></CustField>
              <CustField label="Unit"><input value={a.unit} onChange={(e) => setAddr(i, { unit: e.target.value })} data-testid={`customer-address-unit-${i}`} className={_inputCls} /></CustField>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <CustField label="Municipality"><input value={a.municipality} onChange={(e) => setAddr(i, { municipality: e.target.value })} data-testid={`customer-address-municipality-${i}`} className={_inputCls} /></CustField>
              <CustField label="Province">
                <select value={a.province} onChange={(e) => setAddr(i, { province: e.target.value })} data-testid={`customer-address-province-${i}`} className={_inputCls}>
                  {CA_PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </CustField>
              <CustField label="Postal code"><input value={a.postal_code} onChange={(e) => setAddr(i, { postal_code: e.target.value.toUpperCase() })} data-testid={`customer-address-postal-${i}`} className={_inputCls} placeholder="K1V 9K1" /></CustField>
            </div>
            <CustField label="Address notes"><textarea value={a.notes} onChange={(e) => setAddr(i, { notes: e.target.value })} data-testid={`customer-address-notes-${i}`} rows={2} className={_inputCls} /></CustField>
            {data.addresses.length > 1 && (
              <button type="button" onClick={() => setData({ ...data, addresses: data.addresses.filter((_, x) => x !== i) })} data-testid={`customer-address-remove-${i}`} className="text-xs text-rose-700 hover:underline">Remove this address</button>
            )}
          </div>
        ))}
        <button type="button" onClick={() => setData({ ...data, addresses: [...data.addresses, _emptyAddress()] })} data-testid="add-address-btn" className="text-xs font-medium text-[#0F4C81] hover:underline">+ Address</button>
      </CustSection>

      <CustSection title="Notes">
        <CustField label="Customer notes"><textarea value={data.notes} onChange={(e) => setData({ ...data, notes: e.target.value })} rows={4} data-testid="customer-notes" className={_inputCls} placeholder="Anything to remember about this customer" /></CustField>
        <CustField label="This customer bills to"><input value={data.bills_to} onChange={(e) => setData({ ...data, bills_to: e.target.value })} data-testid="customer-bills-to" className={_inputCls} placeholder="Parent account, property manager, etc." /></CustField>
      </CustSection>

      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={data.send_notifications} onChange={(e) => setData({ ...data, send_notifications: e.target.checked })} data-testid="customer-send-notifications" />
          Send notifications
        </label>
        <div className="flex gap-3">
          <button type="button" onClick={() => nav(-1)} className="px-5 py-2 border border-slate-300 text-slate-700 text-sm rounded-md hover:bg-slate-50" data-testid="customer-cancel-btn">Cancel</button>
          <button type="submit" data-testid="customer-save-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium">{isNew ? "Create customer" : "Save changes"}</button>
        </div>
      </div>
    </form>
  );
}

export function CustomerDetail() {
  // Wrap the real component in a render-crash catcher. Legacy data shapes in
  // production (e.g. a customer record whose `emails` field is a string or
  // an array of {email:...} objects instead of an array of plain strings)
  // would previously throw inside .map() or render-an-object-as-a-child, and
  // React would unmount the whole subtree — the user saw only the background.
  // The try/catch inside load() only guarded network errors, not render.
  return (
    <CustomerDetailBoundary>
      <CustomerDetailInner />
    </CustomerDetailBoundary>
  );
}


class CustomerDetailBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    // Surface in prod devtools + our own log; safe (no PII).
    console.error("[CustomerDetail render crashed]", err, info?.componentStack);
  }
  reset = () => this.setState({ err: null });
  render() {
    if (!this.state.err) return this.props.children;
    const msg = (this.state.err && (this.state.err.message || String(this.state.err))) || "Unknown render error";
    return (
      <div data-testid="customer-detail-crash" className="max-w-lg mx-auto mt-8 space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="font-semibold text-red-800 mb-1">Could not display customer</div>
          <div className="text-sm text-red-700 mb-2">The page hit an unexpected error while rendering this customer&apos;s data.</div>
          <div className="text-xs text-red-600 font-mono bg-red-100/60 border border-red-200 rounded p-2 mb-3 break-all">{msg}</div>
          <div className="flex gap-2">
            <button onClick={this.reset} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md" data-testid="customer-detail-crash-retry">Try again</button>
            <Link to="/customers" className="px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded-md hover:bg-slate-50">Back to customers</Link>
          </div>
        </div>
      </div>
    );
  }
}


// --- Helpers to normalise legacy customer data shapes before render ---
function _asEmailList(c) {
  // Accepts: array of strings, array of {email|address} objects, single string, or nothing.
  const raw = c?.emails;
  const out = [];
  if (Array.isArray(raw)) {
    for (const e of raw) {
      if (typeof e === "string" && e.trim()) out.push(e.trim());
      else if (e && typeof e === "object") {
        const s = e.email || e.address || e.value || "";
        if (typeof s === "string" && s.trim()) out.push(s.trim());
      }
    }
  } else if (typeof raw === "string" && raw.trim()) {
    out.push(raw.trim());
  }
  if (!out.length && typeof c?.email === "string" && c.email.trim()) out.push(c.email.trim());
  return out;
}

function _asPhoneList(c) {
  // Accepts: array of {label,number}, array of strings, single string, or nothing.
  const raw = c?.phones;
  const out = [];
  const push = (label, num) => {
    if (typeof num === "string" && num.trim()) out.push({ label: label || "", number: num.trim() });
  };
  if (Array.isArray(raw)) {
    for (const p of raw) {
      if (typeof p === "string") push("", p);
      else if (p && typeof p === "object") push(p.label || p.type || "", p.number || p.value || p.tel || "");
    }
  } else if (typeof raw === "string") {
    push("", raw);
  }
  if (!out.length && typeof c?.phone === "string" && c.phone.trim()) push("", c.phone);
  return out;
}

function _asAddressList(c) {
  const raw = c?.addresses;
  if (!Array.isArray(raw)) return [];
  return raw.filter((a) => a && typeof a === "object");
}


function CustomerDetailInner() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [hist, setHist] = useState({ estimates: [], invoices: [], jobs: [], attachments: [] });
  const [tab, setTab] = useState("profile");
  const [showQuickQuote, setShowQuickQuote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [inviteState, setInviteState] = useState({ busy: false, msg: "", err: "", link: null });
  const [privateNotes, setPrivateNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [savedNotesAt, setSavedNotesAt] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [showDnsModal, setShowDnsModal] = useState(false);
  const [dnsReason, setDnsReason] = useState("");
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Per-tab search + Jobs sub-filter (screenshot: All / Scheduled / Not scheduled / In Progress / Completed)
  const [jobSearch, setJobSearch] = useState("");
  const [jobFilter, setJobFilter] = useState("all");
  const [selectedJobs, setSelectedJobs] = useState(() => new Set());
  const [invSearch, setInvSearch] = useState("");
  const [estSearch, setEstSearch] = useState("");

  const toggleDoNotService = async (next, reason = "") => {
    const payload = {
      ...c,
      do_not_service: next,
      do_not_service_reason: next ? (reason || c.do_not_service_reason || "") : "",
    };
    delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload._id;
    const { data } = await api.put(`/customers/${id}`, payload);
    setC(data);
    setShowDnsModal(false);
    setDnsReason("");
  };

  const savePrivateNotes = async () => {
    setSavingNotes(true);
    try {
      const payload = { ...c, private_notes: privateNotes };
      delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload._id;
      const { data } = await api.put(`/customers/${id}`, payload);
      setC(data);
      setSavedNotesAt(new Date());
    } finally { setSavingNotes(false); }
  };

  const sendPortalInvite = async () => {
    setInviteState({ busy: true, msg: "", err: "", link: null });
    try {
      const { data } = await api.post(`/customers/${id}/portal-invite`);
      const msg = data.stubbed
        ? `Email not configured — copy this link to test: `
        : `Portal invite sent to ${data.to}`;
      setInviteState({ busy: false, msg, err: "", link: data.stubbed ? data.link : null });
      setTimeout(() => setInviteState((s) => ({ ...s, msg: "", link: null })), 30000);
    } catch (e) {
      setInviteState({ busy: false, msg: "", err: formatErr(e), link: null });
    }
  };

  const load = async () => {
    // (Jul 15, 2026) Wrapped in try/catch — previously an API error (e.g.
    // stale token after a hard-nav row-click on production) left `c` null
    // forever and the page rendered as a blank content area behind the chrome.
    // Now we always exit the loading state, even on failure, and surface the
    // error to the user.
    setLoadErr("");
    try {
      const [{ data: cust }, { data: h }] = await Promise.all([
        api.get(`/customers/${id}`),
        api.get(`/customers/${id}/history`),
      ]);
      setC(cust); setHist(h);
      setPrivateNotes(cust.private_notes || "");
    } catch (e) {
      setLoadErr(formatErr(e) || "Could not load customer.");
    }
  };
  useEffect(() => { load(); }, [id]);

  const [confirmDlg, setConfirmDlg] = useState(null);
  const del = () => {
    setConfirmDlg({
      title: `Delete customer ${c?.name || ""}?`,
      body: "This soft-deletes the customer. Their historical jobs, invoices and estimates remain in reporting.",
      confirmLabel: "Delete customer",
      onConfirm: async () => {
        await api.delete(`/customers/${id}`);
        nav("/customers");
      },
    });
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/customers/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } finally { setUploading(false); e.target.value = ""; }
  };

  const delAtt = (aid, fname) => {
    setConfirmDlg({
      title: `Delete attachment "${fname || ""}"?`,
      body: "This permanently removes the file. This cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: async () => { await api.delete(`/attachments/${aid}`); await load(); },
    });
  };

  if (loadErr) {
    return (
      <div data-testid="customer-detail-error" className="max-w-lg mx-auto mt-8 space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="font-semibold text-red-800 mb-1">Could not load customer</div>
          <div className="text-sm text-red-700 mb-3">{loadErr}</div>
          <div className="flex gap-2">
            <button onClick={load} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md" data-testid="customer-detail-retry">Retry</button>
            <Link to="/customers" className="px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded-md hover:bg-slate-50">Back to customers</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!c) return <div data-testid="customer-detail-loading" className="text-sm text-slate-500 p-6">Loading customer…</div>;

  // Normalise legacy data shapes so render never trips on unexpected types.
  const emailList = _asEmailList(c);
  const phoneList = _asPhoneList(c);
  const addressList = _asAddressList(c);

  return (
    <div data-testid="customer-detail" className="space-y-5">
      {/* Breadcrumb */}
      <nav className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="customer-breadcrumb">
        <button onClick={() => nav(-1)} className="p-1 hover:bg-slate-100 rounded"><ArrowLeft size={14} /></button>
        <Link to="/customers" className="hover:text-slate-900">Customers</Link>
        <ChevronRight size={12} />
        <span className="text-slate-900 font-medium truncate">{c.name}</span>
      </nav>

      {/* Title row: name + inline edit → right side HCP-style pill actions */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 break-words">{c.name}</h1>
            <Link to={`/customers/${id}/edit`} data-testid="edit-customer-btn" className="p-1.5 text-slate-400 hover:text-[#0F4C81] rounded hover:bg-slate-100" title="Edit customer"><Edit size={15} /></Link>
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-slate-500 flex-wrap">
            {c.company_name && <span>{c.company_name}</span>}
            {c.customer_type && <span className="capitalize text-[11px] uppercase tracking-wider bg-slate-100 px-1.5 py-0.5 rounded">{c.customer_type === "commercial" ? "Business" : c.customer_type === "residential" ? "Homeowner" : c.customer_type}</span>}
            {c.do_not_service && <span className="text-[11px] uppercase tracking-wider bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-bold">DO NOT SERVICE</span>}
          </div>
        </div>

        {/* HCP-style pill actions: +Job, +Estimate + 3-dot menu */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap" data-testid="customer-quick-actions">
          <Link to={`/jobs/new?customer_id=${id}`} data-testid="qa-new-job-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white text-sm font-medium transition-colors">
            <Plus size={14}/> Job
          </Link>
          <Link to={`/estimates/new?customer=${id}`} data-testid="qa-new-estimate-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white text-sm font-medium transition-colors">
            <Plus size={14}/> Estimate
          </Link>
          <button onClick={() => setShowQuickQuote(true)} data-testid="qa-quick-quote-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-sm font-medium shadow-sm"><Zap size={14}/> Quick Quote</button>
          <div className="relative">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} data-testid="customer-more-menu-btn" className="p-2 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50" title="More actions">
              <MoreVertical size={16}/>
            </button>
            {showMoreMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowMoreMenu(false)} />
                <div data-testid="customer-more-menu" className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-md shadow-lg z-40 py-1 text-sm">
                  <button onClick={() => { setShowMoreMenu(false); sendPortalInvite(); }} disabled={inviteState.busy || !c.email} title={!c.email ? "Customer has no email on file" : "Email a magic-link to the customer portal"} data-testid="send-portal-invite-btn" className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"><Send size={14}/>{inviteState.busy ? "Sending…" : "Send Portal Link"}</button>
                  {c.do_not_service ? (
                    <button onClick={() => { setShowMoreMenu(false); toggleDoNotService(false); }} data-testid="clear-dns-btn" className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-700 flex items-center gap-2"><Ban size={14}/> Clear DO NOT SERVICE</button>
                  ) : (
                    <button onClick={() => { setShowMoreMenu(false); setShowDnsModal(true); }} data-testid="set-dns-btn" className="w-full text-left px-3 py-2 hover:bg-red-50 text-slate-700 hover:text-red-700 flex items-center gap-2"><Ban size={14}/> Mark DO NOT SERVICE</button>
                  )}
                  <div className="h-px bg-slate-100 my-1" />
                  <button onClick={() => { setShowMoreMenu(false); del(); }} data-testid="delete-customer-btn" className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 flex items-center gap-2"><Trash2 size={14}/> Delete customer</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {c.do_not_service && (
        <div data-testid="dns-banner" className="bg-red-50 border-l-4 border-red-600 px-4 py-3 rounded flex items-start gap-3">
          <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <div className="font-semibold text-red-900 text-sm tracking-wide">DO NOT SERVICE</div>
            {c.do_not_service_reason && <div className="text-sm text-red-800 mt-0.5">{c.do_not_service_reason}</div>}
          </div>
        </div>
      )}

      {inviteState.msg && (
        <div data-testid="portal-invite-msg" className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 rounded flex items-center gap-2 flex-wrap">
          <CheckCircle2 size={14} className="shrink-0" /> {inviteState.msg}
          {inviteState.link && <a href={inviteState.link} target="_blank" rel="noopener noreferrer" className="text-[#0F4C81] underline break-all">{inviteState.link}</a>}
        </div>
      )}
      {inviteState.err && <div data-testid="portal-invite-err" className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded">{inviteState.err}</div>}

      {/* Tabs — order matches HCP screenshot; Leads intentionally omitted (no lead pipeline in this codebase) */}
      <div className="border-b border-slate-200 flex gap-1 overflow-x-auto" data-testid="customer-tabs">
        {[
          { key: "profile", label: "Profile", count: null },
          { key: "jobs", label: "Jobs", count: hist.jobs?.length || 0 },
          { key: "invoices", label: "Invoices", count: hist.invoices?.length || 0 },
          { key: "estimates", label: "Estimates", count: hist.estimates?.length || 0 },
          { key: "attachments", label: "Attachments", count: hist.attachments?.length || 0 },
          { key: "notes", label: "Notes", count: null },
        ].map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} className={`shrink-0 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#0F4C81] text-[#0F4C81]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            {t.label}{t.count != null && t.count > 0 && <span className="ml-1.5 text-xs text-slate-400 font-normal">({t.count})</span>}
          </button>
        ))}
      </div>

      {/* PROFILE TAB */}
      {tab === "profile" && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="tab-profile-content">
          <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
            <h3 className="font-semibold text-sm text-slate-900">Contact</h3>
            <div className="text-sm space-y-1.5">
              {emailList.map((em, i) => (
                <div key={`em-${i}`}><span className="text-slate-500 text-xs uppercase tracking-wider">Email · </span>{em}</div>
              ))}
              {phoneList.map((ph, i) => (
                <div key={`ph-${i}`}><span className="text-slate-500 text-xs uppercase tracking-wider">{ph.label ? `${ph.label} · ` : "Phone · "}</span>{ph.number}</div>
              ))}
              {c.role && <div><span className="text-slate-500 text-xs uppercase tracking-wider">Role · </span>{c.role}</div>}
              {c.send_notifications === false && <div className="text-xs text-slate-500 italic">Notifications disabled</div>}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3 lg:col-span-2">
            <h3 className="font-semibold text-sm text-slate-900">Addresses</h3>
            {addressList.length > 0 ? (
              <div className="space-y-2 text-sm">
                {addressList.map((a, i) => {
                  const line1 = [a.street, a.unit].filter(Boolean).join(" ");
                  const line2 = [a.municipality, a.province, a.postal_code].filter(Boolean).join(" ");
                  return (
                    <div key={`addr-${i}`} className="border-l-2 border-slate-200 pl-3">
                      {line1 && <div>{line1}</div>}
                      {line2 && <div>{line2}</div>}
                      {a.notes && <div className="text-xs text-slate-500 italic mt-0.5">{a.notes}</div>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm">
                <div className="text-xs text-slate-500 uppercase tracking-wider">Billing</div>
                <div className="whitespace-pre-line">{c.billing_address || "—"}</div>
              </div>
            )}
            {c.bills_to && <div className="text-xs pt-2 border-t border-slate-100"><span className="text-slate-500 uppercase tracking-wider">Bills to · </span>{c.bills_to}</div>}
            {c.notes && <div className="text-sm text-slate-600 border-t border-slate-100 pt-3"><div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Notes (public)</div>{c.notes}</div>}
          </div>
        </div>
      )}

      {/* JOBS TAB — HCP-style table */}
      {tab === "jobs" && (() => {
        const jobs = hist.jobs || [];
        const q = jobSearch.trim().toLowerCase();
        const rows = jobs.filter((j) => {
          if (jobFilter === "scheduled") { if (j.status !== "Scheduled") return false; }
          else if (jobFilter === "not_scheduled") { if (j.scheduled_start) return false; if (j.status === "Completed" || j.status === "Cancelled") return false; }
          else if (jobFilter === "in_progress") { if (j.status !== "In Progress" && j.status !== "On My Way") return false; }
          else if (jobFilter === "completed") { if (j.status !== "Completed") return false; }
          if (!q) return true;
          const li = Array.isArray(j.line_items) ? j.line_items : [];
          const liText = li.map((x) => `${x?.name || ""} ${x?.description || ""}`).join(" ").toLowerCase();
          const desc = (j.description || j.job_type || j.notes || "").toLowerCase();
          const addr = (j.service_address || c.billing_address || "").toLowerCase();
          const num = (j.number || "").toLowerCase();
          return desc.includes(q) || addr.includes(q) || num.includes(q) || liText.includes(q);
        });
        const subFilters = [
          { key: "all", label: "All", count: jobs.length },
          { key: "scheduled", label: "Scheduled", count: jobs.filter((j) => j.status === "Scheduled").length },
          { key: "not_scheduled", label: "Not scheduled", count: jobs.filter((j) => !j.scheduled_start && j.status !== "Completed" && j.status !== "Cancelled").length },
          { key: "in_progress", label: "In Progress", count: jobs.filter((j) => j.status === "In Progress" || j.status === "On My Way").length },
          { key: "completed", label: "Completed", count: jobs.filter((j) => j.status === "Completed").length },
        ];
        const visibleIds = rows.map((r) => r.id);
        const allSelected = visibleIds.length > 0 && visibleIds.every((rid) => selectedJobs.has(rid));
        const toggleAll = () => {
          const next = new Set(selectedJobs);
          if (allSelected) visibleIds.forEach((rid) => next.delete(rid));
          else visibleIds.forEach((rid) => next.add(rid));
          setSelectedJobs(next);
        };
        const toggleOne = (rid) => {
          const next = new Set(selectedJobs);
          if (next.has(rid)) next.delete(rid); else next.add(rid);
          setSelectedJobs(next);
        };
        return (
          <div className="space-y-3" data-testid="tab-jobs-content">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="customer-jobs-search" value={jobSearch} onChange={(e) => setJobSearch(e.target.value)} placeholder="Search jobs" className="pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md w-64 focus:outline-none focus:border-[#0F4C81]" />
              </div>
              {selectedJobs.size > 0 && (
                <div data-testid="customer-jobs-selected-count" className="text-xs text-slate-500 bg-slate-100 border border-slate-200 px-2.5 py-1.5 rounded">
                  {selectedJobs.size} selected · <button onClick={() => setSelectedJobs(new Set())} className="text-[#0F4C81] hover:underline">clear</button>
                </div>
              )}
            </div>
            <div className="flex gap-4 border-b border-slate-200" data-testid="customer-jobs-subfilters">
              {subFilters.map((f) => (
                <button key={f.key} onClick={() => setJobFilter(f.key)} data-testid={`jobs-filter-${f.key}`} className={`pb-2 text-sm font-medium border-b-2 -mb-px transition-colors ${jobFilter === f.key ? "border-[#0F4C81] text-[#0F4C81]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                  {f.label}{f.count > 0 && <span className="ml-1 text-xs text-slate-400">({f.count})</span>}
                </button>
              ))}
            </div>
            {rows.length === 0 ? (
              <div className="text-sm text-slate-500 py-10 text-center bg-slate-50 rounded border border-slate-200">
                {jobs.length === 0 ? (
                  <>No jobs yet. <Link to={`/jobs/new?customer_id=${id}`} className="text-[#0F4C81] hover:underline">Create the first one</Link></>
                ) : "No jobs match your search or filter."}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                        <th className="px-3 py-3 w-8"><input type="checkbox" data-testid="customer-jobs-select-all" checked={allSelected} onChange={toggleAll} /></th>
                        <th className="px-4 py-3 text-left">Job #</th>
                        <th className="px-4 py-3 text-left">Customer name</th>
                        <th className="px-4 py-3 text-left">Job created date</th>
                        <th className="px-4 py-3 text-left">Job scheduled start date</th>
                        <th className="px-4 py-3 text-left">Job completed date</th>
                        <th className="px-4 py-3 text-left">Address</th>
                        <th className="px-4 py-3 text-left">Job description</th>
                        <th className="px-4 py-3 text-right">Job amount</th>
                        <th className="px-4 py-3 text-left">Job status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((j) => {
                        const isSel = selectedJobs.has(j.id);
                        // Description column pulls from the invoice line items first — that's where
                        // the real work label lives (e.g. "Plumbing: job location Gabriel pizza").
                        // Falls back to job.description → job_type → notes if there are no line items.
                        const li = Array.isArray(j.line_items) ? j.line_items.filter((x) => (x?.name || x?.description || "").trim()) : [];
                        let descText = "";
                        if (li.length > 0) {
                          const primary = (li[0].name || li[0].description || "").trim();
                          descText = li.length > 1 ? `${primary} (+${li.length - 1} more)` : primary;
                        } else {
                          descText = (j.description || j.job_type || j.notes || "").trim();
                        }
                        return (
                          <tr key={j.id} data-testid={`customer-job-row-${j.id}`} onClick={() => nav(`/jobs/${j.id}`)} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer ${isSel ? "bg-blue-50/40" : ""}`}>
                            <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); toggleOne(j.id); }}>
                              <input type="checkbox" data-testid={`customer-job-select-${j.id}`} checked={isSel} onChange={() => toggleOne(j.id)} />
                            </td>
                            <td className="px-4 py-3 font-mono font-medium">
                              <Link to={`/jobs/${j.id}`} onClick={(e) => e.stopPropagation()} data-testid={`customer-job-link-${j.id}`} className="text-[#0F4C81] hover:underline">{j.number || "—"}</Link>
                            </td>
                            <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{c.name || "—"}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDateTime(j.created_at)}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{j.scheduled_start ? fmtDateTime(j.scheduled_start) : <span className="text-slate-400">—</span>}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{j.finished_at ? fmtDateTime(j.finished_at) : <span className="text-slate-400">—</span>}</td>
                            <td className="px-4 py-3 text-slate-700 max-w-[220px] truncate" title={j.service_address || c.billing_address || ""}>{j.service_address || c.billing_address || <span className="text-slate-400">—</span>}</td>
                            <td className="px-4 py-3 text-slate-700 max-w-[280px] truncate" title={descText}>{descText || <span className="text-slate-400">—</span>}</td>
                            <td className="px-4 py-3 text-right font-medium tabular whitespace-nowrap">{fmtCAD(j.total)}</td>
                            <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${statusClass(j.status || "Draft")}`}>{j.status || "Draft"}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* INVOICES TAB — HCP-style table (same job records that carry an invoice_status) */}
      {tab === "invoices" && (() => {
        const invs = hist.invoices || [];
        const q = invSearch.trim().toLowerCase();
        const rows = invs.filter((i) => {
          if (!q) return true;
          return (i.number || "").toLowerCase().includes(q) || (i.status || "").toLowerCase().includes(q);
        });
        return (
          <div className="space-y-3" data-testid="tab-invoices-content">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="customer-invoices-search" value={invSearch} onChange={(e) => setInvSearch(e.target.value)} placeholder="Search invoices" className="pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md w-64 focus:outline-none focus:border-[#0F4C81]" />
              </div>
              <div className="text-xs text-slate-500">Every invoice is the same record as its job — click a row to open.</div>
            </div>
            {rows.length === 0 ? (
              <div className="text-sm text-slate-500 py-10 text-center bg-slate-50 rounded border border-slate-200">
                {invs.length === 0 ? (
                  <>No invoices yet. Invoicing starts from a job — <Link to={`/jobs/new?customer_id=${id}`} className="text-[#0F4C81] hover:underline">create one</Link>.</>
                ) : "No invoices match your search."}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                        <th className="px-4 py-3 text-left">Invoice #</th>
                        <th className="px-4 py-3 text-left">Job #</th>
                        <th className="px-4 py-3 text-left">Issued</th>
                        <th className="px-4 py-3 text-left">Due</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-right">Balance</th>
                        <th className="px-4 py-3 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((i) => {
                        const paid = (i.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
                        const balance = Math.max(Number(i.total || 0) - paid, 0);
                        return (
                          <tr key={i.id} data-testid={`customer-invoice-row-${i.id}`} onClick={() => nav(`/jobs/${i.id}`)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                            <td className="px-4 py-3 font-mono font-medium text-[#0F4C81]">{i.number || "—"}</td>
                            <td className="px-4 py-3 font-mono text-slate-500">{i.job_number || i.number || "—"}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(i.created_at)}</td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(i.due_date)}</td>
                            <td className="px-4 py-3 text-right font-medium tabular">{fmtCAD(i.total)}</td>
                            <td className="px-4 py-3 text-right tabular text-slate-700">{fmtCAD(balance)}</td>
                            <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${statusClass(i.status || "Draft")}`}>{i.status || "Draft"}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ESTIMATES TAB — HCP-style table */}
      {tab === "estimates" && (() => {
        const ests = hist.estimates || [];
        const q = estSearch.trim().toLowerCase();
        const rows = ests.filter((e) => {
          if (!q) return true;
          return (e.number || "").toLowerCase().includes(q) || (e.status || "").toLowerCase().includes(q);
        });
        return (
          <div className="space-y-3" data-testid="tab-estimates-content">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="customer-estimates-search" value={estSearch} onChange={(e) => setEstSearch(e.target.value)} placeholder="Search estimates" className="pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md w-64 focus:outline-none focus:border-[#0F4C81]" />
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="text-sm text-slate-500 py-10 text-center bg-slate-50 rounded border border-slate-200">
                {ests.length === 0 ? (
                  <>No estimates yet. <Link to={`/estimates/new?customer=${id}`} className="text-[#0F4C81] hover:underline">Create one</Link>.</>
                ) : "No estimates match your search."}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                        <th className="px-4 py-3 text-left">Estimate #</th>
                        <th className="px-4 py-3 text-left">Created</th>
                        <th className="px-4 py-3 text-left">Expires</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((e) => (
                        <tr key={e.id} data-testid={`customer-estimate-row-${e.id}`} onClick={() => nav(`/estimates/${e.id}`)} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                          <td className="px-4 py-3 font-mono font-medium text-[#0F4C81]">{e.number || "—"}</td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(e.expiration_date || e.expires_at)}</td>
                          <td className="px-4 py-3 text-right font-medium tabular">{fmtCAD(e.total)}</td>
                          <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${statusClass(e.status || "Draft")}`}>{e.status || "Draft"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ATTACHMENTS TAB (unchanged behaviour, cleaner layout) */}
      {tab === "attachments" && (
        <div className="space-y-3" data-testid="tab-attachments-content">
          <label data-testid="upload-attachment-btn" className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 cursor-pointer">
            <Upload size={14} />{uploading ? "Uploading..." : "Upload file"}
            <input type="file" className="hidden" onChange={uploadFile} disabled={uploading} />
          </label>
          {hist.attachments.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center bg-slate-50 rounded border border-slate-200">No attachments yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {hist.attachments.map((a) => (
                <div key={a.id} className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-3">
                  <Paperclip size={16} className="text-slate-400 shrink-0" />
                  <a href={fileUrl(`/attachments/${a.id}/download`)} target="_blank" rel="noopener noreferrer" className="flex-1 text-sm hover:text-[#0F4C81] truncate">{a.filename}</a>
                  <button onClick={() => delAtt(a.id, a.filename)} data-testid={`customer-attachment-delete-${a.id}`} className="text-slate-400 hover:text-red-600 shrink-0"><X size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* NOTES TAB — private notes (internal only, never on PDF/portal) */}
      {tab === "notes" && (
        <div className="bg-amber-50/60 border border-amber-200 rounded-md p-5" data-testid="tab-notes-content">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm text-amber-900 flex items-center gap-1.5"><Lock size={13} /> Private notes <span className="text-[10px] font-normal uppercase tracking-wider text-amber-700">internal only · never shown to customer</span></h3>
            <div className="flex items-center gap-2">
              {savedNotesAt && <span className="text-[11px] text-slate-500">Saved {new Date(savedNotesAt).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })}</span>}
              <button onClick={savePrivateNotes} disabled={savingNotes || privateNotes === (c.private_notes || "")} data-testid="save-customer-private-notes-btn-tab" className="px-3 py-1.5 text-xs bg-amber-700 text-white rounded hover:bg-amber-800 disabled:opacity-40 flex items-center gap-1"><Save size={11}/>{savingNotes ? "Saving…" : "Save"}</button>
            </div>
          </div>
          <textarea
            data-testid="customer-private-notes-input-tab"
            value={privateNotes}
            onChange={(e) => setPrivateNotes(e.target.value)}
            rows={8}
            placeholder="Add private notes about this customer (gate codes, access notes, behavior, payment history, etc.)"
            className="w-full px-3 py-2 text-sm bg-white border border-amber-200 rounded focus:outline-none focus:border-amber-500 resize-y"
          />
        </div>
      )}

      {/* Modals */}
      {showQuickQuote && <QuickQuoteModal customerId={id} customerName={c.name} onClose={() => setShowQuickQuote(false)} onCreated={() => { setShowQuickQuote(false); load(); }} />}
      <ConfirmDialog dialog={confirmDlg} onClose={() => setConfirmDlg(null)} />
      {showDnsModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowDnsModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-6 shadow-xl space-y-4" data-testid="dns-modal">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0"><Ban size={20}/></div>
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">Mark customer as DO NOT SERVICE?</h3>
                <p className="text-sm text-slate-500 mt-1">This adds a prominent red banner on this customer everywhere, but doesn't delete their history.</p>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Reason (optional)</label>
              <textarea data-testid="dns-reason-input" value={dnsReason} onChange={(e) => setDnsReason(e.target.value)} rows={5} placeholder="e.g. Non-payment of invoice 00012, customer abuse, unsafe site…" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-red-500 resize-y" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowDnsModal(false)} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
              <button onClick={() => toggleDoNotService(true, dnsReason)} data-testid="dns-confirm-btn" className="px-4 py-2 bg-red-600 text-white text-sm rounded-md font-medium hover:bg-red-700 flex items-center gap-1.5"><Ban size={14}/> Mark DO NOT SERVICE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
